import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _size from 'lodash/size';
import PropTypes from 'prop-types';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mScenario from 'models/scenario';
// Custom
import {
  openDialog, refreshList, getInstallationScenarios, deleteInstallationScenario, duplicateInstallationScenario, triggerInstallationScenario,
} from 'actions';
import { scenariosShape } from 'constants/PropTypesShapes';
import { AddTextButton, AntipassbackTextButton } from 'components/buttons/TextButtons';
import SubListTemplate from 'components/templates/SubListTemplate';
import withConfirmDialog from 'hocs/WithConfirmDialog';
import withPermissions from 'hocs/WithPermissions';
import { scenariosAddPath, navigateToScenariosAdd, navigateToScenariosShow } from 'installations/navigation';
import withEntitiesInformation from 'installations/assets/hocs/WithEntitiesInformation';
import withResources from 'installations/assets/hocs/WithResources';
import ScenarioRow from 'installations/assets/scenarios/ScenarioRow';
import ScenarioRowDetails from 'installations/assets/scenarios/ScenarioRowDetails';

class InstallationScenarios extends React.Component {
  constructor(props) {
    super(props);

    this.handleDelete = this.handleDelete.bind(this);
    this.handleDuplicate = this.handleDuplicate.bind(this);
    this.handleTrigger = this.handleTrigger.bind(this);
    this.handleRowDbleClick = this.handleRowDbleClick.bind(this);
    this.handleDisplayAntipassbackDetails = this.handleDisplayAntipassbackDetails.bind(this);
    this.doGetRowComp = this.doGetRowComp.bind(this);
    this.doGetRowCompDetails = this.doGetRowCompDetails.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { deleted, handleRefreshList } = this.props;
    // Operations catching
    if (!prevProps.deleted && deleted) {
      handleRefreshList();
    }
  }

  // Handlers
  handleDelete(scenario) {
    const { params, handleDisplayConfirm, handleDeleteScenario } = this.props;
    handleDisplayConfirm(
      { id: 'confirm.deleteScenario', values: { scenarioName: scenario.name } },
      () => handleDeleteScenario(params.id, scenario.id),
    );
  }

  handleDuplicate(scenario) {
    const { params, handleDuplicateScenario } = this.props;
    handleDuplicateScenario(scenario);
    navigateToScenariosAdd(params.id);
  }

  handleTrigger(scenario) {
    const { params, handleTriggerScenario } = this.props;
    handleTriggerScenario(params.id, scenario.id, scenario.name);
  }

  handleRowDbleClick(scenario) {
    const { params, hasPermission } = this.props;
    const { pInstallation: { configWrite } } = permissions;
    if (scenario && hasPermission({ needed: configWrite, permissionsOrContext: 'installationPermissions' })) {
      // Display scenario
      navigateToScenariosShow(params.id, scenario.id);
    }
  }

  handleDisplayAntipassbackDetails() {
    const { handleOpenDialog } = this.props;
    handleOpenDialog({
      title: {
        id: 'title.installationAntipassback',
      },
      actions: [
        { type: 'close' },
      ],
      component: {
        type: 'INSTALLATION_ANTIPASSBACK_DETAILS',
      },
      size: 'md',
    });
  }

  // Private
  doGetRowComp(props) {
    const { params } = this.props;
    return (
      <ScenarioRow
        {...props}
        id={params.id}
        onDelete={this.handleDelete}
        onTrigger={this.handleTrigger}
      />
    );
  }

  doGetRowCompDetails(props) {
    return (
      <ScenarioRowDetails
        {...props}
        onDuplicate={this.handleDuplicate}
        onTrigger={this.handleTrigger}
      />
    );
  }

  render() {
    const {
      params, scenarios, desynchronized, hasPermission, handleGetScenarios,
    } = this.props;
    const sortedScenarios = mScenario.sortElements(scenarios.elements);

    // For the sub list, create the good structure
    const scenariosList = {
      loading: scenarios.loading,
      elements: {
        data: sortedScenarios,
        totalEntries: _size(sortedScenarios),
      },
    };

    // Permissions
    const { pInstallation: { configWrite } } = permissions;

    return (
      <SubListTemplate
        title={<LocalizedMessage id="title.scenariosList" />}
        displayRefresh
        desynchronized={desynchronized}
        actionButtons={[
          <OnlyWith
            key="bAdd"
            permissions={{
              needed: configWrite,
              permissionsOrContext: 'installationPermissions',
            }}
          >
            <AddTextButton primary path={scenariosAddPath(params.id)} />
          </OnlyWith>,
        ]}
        extraActionButtons={[<AntipassbackTextButton key="bApb" onClick={this.handleDisplayAntipassbackDetails} />]}
        name={mScenario.name}
        listFeatures={mScenario.listFeatures}
        withQueryParams={false}
        withDetails
        withActions={hasPermission({ needed: configWrite, permissionsOrContext: 'installationPermissions' })}
        nbActions={2}
        elementsData={{
          loading: scenariosList.loading,
          elements: scenariosList.elements,
          handleGetList: (p, s, f, firstLoad) => handleGetScenarios(params.id, firstLoad),
        }}
        onRowDbleClick={this.handleRowDbleClick}
        withPagination={false}
        withVirtualization={false}
        rowComp={this.doGetRowComp}
        rowCompDetails={this.doGetRowCompDetails}
      />
    );
  }
}

InstallationScenarios.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  scenarios: scenariosShape.isRequired,
  deleted: PropTypes.bool.isRequired,
  desynchronized: PropTypes.bool.isRequired,
  hasPermission: PropTypes.func.isRequired,
  handleDisplayConfirm: PropTypes.func.isRequired,
  handleOpenDialog: PropTypes.func.isRequired,
  handleRefreshList: PropTypes.func.isRequired,
  handleGetScenarios: PropTypes.func.isRequired,
  handleDeleteScenario: PropTypes.func.isRequired,
  handleDuplicateScenario: PropTypes.func.isRequired,
  handleTriggerScenario: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  scenarios: state.installationScenario.list,
  deleted: state.installationScenario.delete.deleted,
  desynchronized: state.installationScenario.list.desynchronized,
});

const mapDispatchToProps = (dispatch) => ({
  handleOpenDialog: (props) => {
    dispatch(openDialog(props));
  },
  handleRefreshList: () => {
    dispatch(refreshList());
  },
  handleGetScenarios: (id, firstLoad) => {
    dispatch(getInstallationScenarios(id, firstLoad));
  },
  handleDeleteScenario: (id, idScenario) => {
    dispatch(deleteInstallationScenario(id, idScenario));
  },
  handleDuplicateScenario: (data) => {
    dispatch(duplicateInstallationScenario(data));
  },
  handleTriggerScenario: (id, idScenario, name) => {
    dispatch(triggerInstallationScenario(id, idScenario, name));
  },
});

module.exports = compose(
  withResources(
    (ownProps) => ({ id: ownProps.params.id }),
    ['doors', 'sources', 'devices'],
  ),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withConfirmDialog,
  withPermissions,
  withEntitiesInformation,
)(InstallationScenarios);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/routes/show/routes/scenarios/routes/index/components/InstallationScenarios.js